import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    // Load from localStorage on init
    const saved = localStorage.getItem('elpampa_cart');
    return saved ? JSON.parse(saved) : [];
  });
  const [isOpen, setIsOpen] = useState(false);
  const [packagingType, setPackagingType] = useState(() => {
    return localStorage.getItem('elpampa_packaging') || 'none';
  });
  const [packagingConfig, setPackagingConfig] = useState(null);
  const saveTimeoutRef = useRef(null);

  // Fetch packaging config
  useEffect(() => {
    const fetchPackagingConfig = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/packaging`);
        setPackagingConfig(response.data);
      } catch (error) {
        console.error('Error fetching packaging config:', error);
      }
    };
    fetchPackagingConfig();
  }, []);

  // Sync cart prices with server on load to ensure prices are current
  useEffect(() => {
    const syncPricesWithServer = async () => {
      if (items.length === 0) return;
      
      try {
        const response = await axios.get(`${BACKEND_URL}/api/products`);
        const serverProducts = response.data;
        
        setItems(prevItems => {
          let hasChanges = false;
          const updatedItems = prevItems.map(item => {
            const serverProduct = serverProducts.find(p => p.slug === item.slug);
            if (serverProduct && serverProduct.price !== item.price) {
              hasChanges = true;
              return { ...item, price: serverProduct.price, image_url: serverProduct.image_url };
            }
            return item;
          }).filter(item => {
            // Remove items that no longer exist or are unavailable
            const serverProduct = serverProducts.find(p => p.slug === item.slug);
            if (!serverProduct) {
              hasChanges = true;
              return false;
            }
            return true;
          });
          
          if (hasChanges) {
            console.log('Cart prices synchronized with server');
          }
          return hasChanges ? updatedItems : prevItems;
        });
      } catch (error) {
        console.error('Error syncing cart prices:', error);
      }
    };
    
    syncPricesWithServer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount

  // Save to localStorage whenever items or packaging change
  useEffect(() => {
    localStorage.setItem('elpampa_cart', JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    localStorage.setItem('elpampa_packaging', packagingType);
  }, [packagingType]);

  // Sync cart to server for logged-in users (for abandoned cart emails)
  // Debounced to avoid too many API calls
  useEffect(() => {
    const syncCartToServer = async () => {
      const token = localStorage.getItem('elpampa_token');
      if (!token || items.length === 0) return;
      
      try {
        await axios.post(
          `${BACKEND_URL}/api/cart/save`,
          { items },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        // Silently fail - this is just for abandoned cart tracking
        console.debug('Cart sync failed (user may not be logged in)');
      }
    };

    // Debounce: wait 2 seconds after last change before syncing
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(syncCartToServer, 2000);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [items]);

  const addItem = (product, quantity = 1) => {
    setItems(prev => {
      const existing = prev.find(item => item.slug === product.slug);
      if (existing) {
        return prev.map(item =>
          item.slug === product.slug
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prev, {
        slug: product.slug,
        name: product.name,
        price: product.price,
        image_url: product.image_url,
        quantity
      }];
    });
  };

  const removeItem = (slug) => {
    setItems(prev => prev.filter(item => item.slug !== slug));
  };

  const updateQuantity = (slug, quantity) => {
    if (quantity <= 0) {
      removeItem(slug);
      return;
    }
    setItems(prev =>
      prev.map(item =>
        item.slug === slug ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = async () => {
    setItems([]);
    setPackagingType('none');
    localStorage.removeItem('elpampa_cart');
    localStorage.removeItem('elpampa_packaging');
    
    // Also clear on server for logged-in users
    const token = localStorage.getItem('elpampa_token');
    if (token) {
      try {
        await axios.delete(`${BACKEND_URL}/api/cart/clear`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        // Silently fail
      }
    }
  };

  const getTotal = () => {
    return items.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const getItemCount = () => {
    return items.reduce((count, item) => count + item.quantity, 0);
  };

  // Calculate packaging cost
  const getPackagingInfo = () => {
    if (!packagingConfig || packagingType === 'none') {
      return { type: 'none', cost: 0, normalBoxes: 0, smallBoxes: 0, description: '' };
    }

    const excluded = packagingConfig.excluded_products || ['picada-de-campo'];
    const normalPrice = packagingConfig.normal_box_price || 1500;
    const smallPrice = packagingConfig.small_box_price || 2000;

    // Filter packagable items
    const packagableItems = items.filter(item => !excluded.includes(item.slug));
    if (packagableItems.length === 0) {
      return { type: 'none', cost: 0, normalBoxes: 0, smallBoxes: 0, description: '' };
    }

    const totalProducts = packagableItems.reduce((sum, item) => sum + item.quantity, 0);
    const cheeseItems = packagableItems.filter(item => item.slug?.toLowerCase().includes('queso'));
    const cheeseCount = cheeseItems.reduce((sum, item) => sum + item.quantity, 0);
    const otherCount = totalProducts - cheeseCount;

    if (packagingType === 'small') {
      if (cheeseCount === 0) {
        return { type: 'none', cost: 0, normalBoxes: 0, smallBoxes: 0, description: 'Caja chica solo para quesos' };
      }
      const smallBoxes = cheeseCount;
      const normalBoxes = otherCount > 0 ? Math.ceil(otherCount / 2) : 0;
      return {
        type: 'mixed',
        cost: (normalBoxes * normalPrice) + (smallBoxes * smallPrice),
        normalBoxes,
        smallBoxes,
        description: `${smallBoxes} caja(s) chica(s)${normalBoxes > 0 ? ` + ${normalBoxes} caja(s) normal(es)` : ''}`
      };
    }

    // Normal boxes
    const normalBoxes = Math.ceil(totalProducts / 2);
    return {
      type: 'normal',
      cost: normalBoxes * normalPrice,
      normalBoxes,
      smallBoxes: 0,
      description: `${normalBoxes} caja(s) premium`
    };
  };

  // Check if packaging is available for current cart
  const canSelectPackaging = () => {
    if (!packagingConfig) return false;
    const excluded = packagingConfig.excluded_products || ['picada-de-campo'];
    return items.some(item => !excluded.includes(item.slug));
  };

  // Check if small box option is available (only if there's cheese)
  const canSelectSmallBox = () => {
    return items.some(item => item.slug?.toLowerCase().includes('queso'));
  };

  const openCart = () => setIsOpen(true);
  const closeCart = () => setIsOpen(false);
  const toggleCart = () => setIsOpen(prev => !prev);

  return (
    <CartContext.Provider value={{
      items,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      getTotal,
      getItemCount,
      isOpen,
      openCart,
      closeCart,
      toggleCart,
      packagingType,
      setPackagingType,
      packagingConfig,
      getPackagingInfo,
      canSelectPackaging,
      canSelectSmallBox
    }}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
